import { Button } from '@/components/Buttons';
import { createPortal } from 'react-dom';
import { ModalProps } from './types';
import { useModalFeature } from '@/features/modal/useModalFeature';
import React, { useId } from 'react';

import {
  modalBodyStyles,
  modalCloseButtonStyles,
  modalContainerVariants,
  modalFooterStyles,
  modalHeaderVariants,
  modalOverlayVariants,
  modalPanelVariants,
  modalStringContentStyles,
  modalTitleStyles,
} from './Modal.styles';

/**
 * Modal
 *
 * A component for displaying content in an overlay dialog. Supports a title, body content,
 * and primary/secondary actions.
 */
export const Modal: React.FC<ModalProps> = ({
  autoFocus = true,
  children,
  className,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  isOpen,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonLabel,
  secondaryButtonLabel,
  showCloseButton = true,
  showCloseButtonLabel = true,
  title,
  ...props
}) => {
  // ==================================================================
  // Feature Hook
  // ==================================================================
  const { closeButtonRef, handleBackdropClick, modalRef, mounted } = useModalFeature({
    autoFocus,
    closeOnBackdropClick,
    closeOnEscape,
    isOpen,
    onClose,
  });

  // ==================================================================
  // IDs for ARIA attributes
  // ==================================================================
  const descriptionId = useId();
  const titleId = useId();

  // ==================================================================
  // Derived state
  // ==================================================================
  const _showTitle = Boolean(title);
  const hasActions = Boolean(primaryButtonLabel || secondaryButtonLabel);
  const ariaLabel = _showTitle ? undefined : 'Modal dialog';
  const ariaLabelledBy = _showTitle ? titleId : undefined;

  // ==================================================================
  // Render nothing until mounted to ensure portal can attach to document.body
  // ==================================================================
  if (!mounted) {
    return null;
  }

  // ==================================================================
  // Class names (cva)
  // ==================================================================
  const containerClassNames = modalContainerVariants({ isOpen });
  const overlayClassNames = modalOverlayVariants({ isOpen });
  const panelClassNames = modalPanelVariants({ className, isOpen });
  const headerClassNames = modalHeaderVariants({ hasTitle: _showTitle });

  // ==================================================================
  // Sub-components for modal structure
  // ==================================================================

  /**
   * Modal header, conditionally rendering title and close button based on props.
   * Renders a built-in <h2> title when `title` prop is provided, and uses ARIA
   * attributes for accessibility. The close button is rendered based on `showCloseButton`
   * and can be auto-focused when the modal opens.
   */
  const ModalHeader = () => {
    if (!title && !showCloseButton) return null;

    return (
      <div className={headerClassNames}>
        {title && (
          <div className="flex flex-1 flex-col">
            <h2 id={titleId} className={modalTitleStyles}>
              {title}
            </h2>
          </div>
        )}

        {showCloseButton && (
          <Button
            aria-label="Close"
            className={modalCloseButtonStyles}
            forwardRef={closeButtonRef}
            iconLeft="X"
            iconOnly={!showCloseButtonLabel}
            label="Close"
            onClick={onClose}
            size="small"
            variant="tertiary"
          />
        )}
      </div>
    );
  };

  /**
   * Modal body that renders the children content. If children is a string,
   * it wraps it in a <p> tag for styling.
   *
   * If there are no children, it returns null.
   */
  const ModalBody = () => {
    if (!children) return null;

    return (
      <div id={descriptionId} className={modalBodyStyles}>
        {typeof children === 'string' ? (
          <p className={modalStringContentStyles}>{children}</p>
        ) : (
          children
        )}
      </div>
    );
  };

  /**
   * Modal footer that renders action buttons based on props.
   *
   * If no action buttons are provided, it returns null.
   */
  const ModalFooter = () => {
    if (!hasActions) return null;

    return (
      <div className={modalFooterStyles}>
        {primaryButtonLabel && (
          <Button
            label={primaryButtonLabel}
            onClick={onPrimaryAction || onClose}
            size="small"
            variant="primary"
          />
        )}

        {secondaryButtonLabel && (
          <Button
            label={secondaryButtonLabel}
            onClick={onSecondaryAction || onClose}
            size="small"
            variant="secondary"
          />
        )}
      </div>
    );
  };

  /**
   * Modal content to be rendered in the portal, including backdrop, overlay, and modal structure.
   *
   * The backdrop captures clicks for closing the modal when `closeOnBackdropClick` is enabled,
   * while the panel contains the header, body, and footer. ARIA attributes are applied for accessibility.
   *
   * The panel click handler stops propagation to prevent backdrop clicks from triggering
   * when interacting with the modal content.
   */
  const modalContent = (
    <div className={containerClassNames} onClick={handleBackdropClick} {...props}>
      <div className={overlayClassNames} />
      <div
        aria-describedby={children ? descriptionId : undefined}
        aria-hidden={!isOpen}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-modal="true"
        className={panelClassNames}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
      >
        <ModalHeader />
        <ModalBody />
        <ModalFooter />
      </div>
    </div>
  );

  // ==================================================================
  // Render the modal content into a portal
  // ==================================================================
  const portalRoot = document.getElementById('portal-root') ?? document.body;
  return createPortal(modalContent, portalRoot);
};

Modal.displayName = 'Modal';

export default Modal;
